import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const STATE = {
  zero_first: ["Would go to 0 first", "text-rose-700"], differs: ["Differs", "text-amber-700"], ok: ["In line", "text-emerald-700"],
  unverified: ["Not verified — no sync", "text-slate-500"], excluded: ["Kept off eBay", "text-slate-400"],
};

function Override({ r, onDone }) {
  const [open, setOpen] = useState(false);
  const [buf, setBuf] = useState(String(r.buffer));
  const [max, setMax] = useState(r.max_qty == null ? "" : String(r.max_qty));
  const save = async (clear) => {
    try {
      await api.put(`/ebay/products/${r.product_legacy_id}/settings`, clear ? { clear: true } : { buffer: Number(buf || 0), max_qty: max === "" ? null : Number(max), unlimited: max === "" });
      setOpen(false); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  if (!open) return <button className="text-xs text-indigo-600" onClick={() => setOpen(true)} data-testid={`ebay-override-${r.staging_id}`}>{r.override ? "Edit override" : "Override"}</button>;
  return (
    <span className="inline-flex items-center gap-1">
      <Input value={buf} onChange={(e) => setBuf(e.target.value)} className="h-7 w-14" title="Buffer" data-testid={`ebay-ovr-buffer-${r.staging_id}`} />
      <Input value={max} onChange={(e) => setMax(e.target.value)} placeholder="∞" className="h-7 w-14" title="Max (blank = unlimited)" data-testid={`ebay-ovr-max-${r.staging_id}`} />
      <Button size="sm" className="h-7" onClick={() => save(false)} data-testid={`ebay-ovr-save-${r.staging_id}`}>Save</Button>
      {r.override && <Button size="sm" variant="ghost" className="h-7" onClick={() => save(true)} data-testid={`ebay-ovr-default-${r.staging_id}`}>Default</Button>}
    </span>
  );
}

export function EbayStockPreview({ accountId, canManage }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => { api.get(`/ebay/accounts/${accountId}/stock-preview`).then((r) => setD(r.data)).catch(() => setD(null)); }, [accountId]);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="ebay-stock-preview">
      <h2 className="text-base font-bold text-slate-900">Stock comparison (read-only)</h2>
      <p className="text-sm text-slate-500 mb-3">What TedOps <i>would</i> send to eBay. Nothing is sent. Default buffer {d.settings.buffer}, max {d.settings.max_qty ?? "unlimited"}.</p>
      <div className="flex flex-wrap gap-4 text-xs mb-3" data-testid="ebay-stock-summary">
        {Object.entries(STATE).map(([k, [l, c]]) => <span key={k} className={c}><b>{d.summary[k] || 0}</b> {l}</span>)}</div>
      <div className="overflow-x-auto"><table className="w-full min-w-[720px] text-sm"><thead><tr className="text-left text-xs text-slate-500">
        <th className="pb-2 font-medium">Listing</th><th className="pb-2 font-medium">TedOps available</th><th className="pb-2 font-medium">Buffer / max</th>
        <th className="pb-2 font-medium">Would send</th><th className="pb-2 font-medium">On eBay now</th><th className="pb-2 font-medium">Status</th></tr></thead>
        <tbody>{d.rows.map((r) => (
          <tr key={r.staging_id} className="border-t border-slate-100" data-testid={`ebay-stock-row-${r.staging_id}`}>
            <td className="py-2 pr-3"><p className="text-slate-900">{r.product_title || r.title}</p><p className="text-xs text-slate-500">#{r.item_id}{r.sku ? ` · ${r.sku}` : ""}</p></td>
            <td className="py-2">{r.tedops_available ?? "—"}</td>
            <td className="py-2 whitespace-nowrap">{r.buffer} / {r.max_qty ?? "∞"}{r.override && <span className="text-[10px] text-indigo-600 ml-1">custom</span>}
              {canManage && r.product_legacy_id && r.state !== "excluded" && <div><Override r={r} onDone={load} /></div>}</td>
            <td className="py-2 font-semibold" data-testid={`ebay-would-send-${r.staging_id}`}>{r.would_send ?? "—"}</td>
            <td className="py-2">{r.ebay_qty}</td>
            <td className={`py-2 text-xs font-medium ${STATE[r.state][1]}`}>{STATE[r.state][0]}</td>
          </tr>))}</tbody></table>
        {d.rows.length === 0 && <p className="text-sm text-slate-400 py-4">Approve some matches to see the comparison.</p>}</div>
    </section>
  );
}
